import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/environment';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Receipt,
  Schedule,
  CheckCircle,
  Warning,
  Error,
  Refresh,
  Timeline,
  Assessment,
  MonetizationOn,
  Email,
  WhatsApp,
  Lock,
  Security,
  Today,
  BarChart as BarChartIcon,
  ArrowBack
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { PeriodFilter } from '../components/ui/PeriodFilter';
import { PeriodType, getPeriodParams, getTodayDates, getYesterdayDates, getLastDaysDates } from '../utils/dateFilters';

interface IntegrationMetrics {
  total_webhooks: number;
  events_by_type: Record<string, number>;
  last_30_days: number;
  last_7_days: number;
  today: number;
  last_updated: string;
}

interface TimelineData {
  date: string;
  total: number;
  events: Record<string, number>;
}

interface EventData {
  id: string;
  event_type: string;
  processed_at: string;
  contact_id: string;
  contact: {
    id: string;
    name: string;
    email: string;
    phone: string;
  } | null;
  webhook_data: any;
}

interface RevenueData {
  total_revenue: number;
  total_transactions: number;
  average_transaction: number;
  revenue_by_event: Record<string, { count: number; revenue: number }>;
  sales_by_product: Record<string, number>;
  currency: string;
}

interface HealthData {
  status: 'healthy' | 'warning' | 'error' | 'no_data';
  message: string;
  last_webhook: {
    time: string;
    event_type: string;
  } | null;
  webhooks_last_24h: number;
  hours_since_last_webhook: number | null;
}

// Mapeamento de nomes de providers
const PROVIDER_NAMES: Record<string, string> = {
  kirvano: 'Kirvano',
  hubla: 'Hubla',
  hotmart: 'Hotmart',
  kiwify: 'Kiwify',
  braip: 'Braip',
  perfectpay: 'Perfect Pay',
  digistore24: 'DigiStore24'
};

const IntegrationMetrics: React.FC = () => {
  const { provider } = useParams<{ provider: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Estados para proteção por senha
  const [isFinanceUnlocked, setIsFinanceUnlocked] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // Estados para filtro de data
  const [dateFilter, setDateFilter] = useState<PeriodType>('general');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Estados para card de vendas geral
  const [salesFilter, setSalesFilter] = useState<PeriodType>('general');
  const [salesDateFrom, setSalesDateFrom] = useState('');
  const [salesDateTo, setSalesDateTo] = useState('');
  const [filteredSales, setFilteredSales] = useState<number | null>(null);
  
  // Estados para os dados
  const [metrics, setMetrics] = useState<IntegrationMetrics | null>(null);
  const [timeline, setTimeline] = useState<TimelineData[]>([]);
  const [events, setEvents] = useState<EventData[]>([]);
  const [revenue, setRevenue] = useState<RevenueData | null>(null);
  const [health, setHealth] = useState<HealthData | null>(null);

  // Cores para os gráficos
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];

  const providerName = provider ? PROVIDER_NAMES[provider.toLowerCase()] || provider : 'Integração';
  const baseUrl = getApiUrl();

  const fetchData = async (period?: PeriodType, customFrom?: string, customTo?: string) => {
    if (!provider) {
      setError('Provider não especificado');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Obter parâmetros de data (apenas para receita)
      const dateParams = getPeriodParams(period || dateFilter, customFrom || dateFrom, customTo || dateTo);

      // Buscar métricas gerais (sempre sem filtro)
      const metricsResponse = await fetch(`${baseUrl}/integrations/${provider}/metrics/overview`);
      const metricsData = await metricsResponse.json();
      if (metricsData.success) {
        setMetrics(metricsData.data);
      }

      // Buscar timeline (sempre sem filtro)
      const timelineResponse = await fetch(`${baseUrl}/integrations/${provider}/metrics/timeline`);
      const timelineData = await timelineResponse.json();
      if (timelineData.success) {
        setTimeline(timelineData.data);
      }

      // Buscar eventos (sempre sem filtro)
      const eventsResponse = await fetch(`${baseUrl}/integrations/${provider}/metrics/events`);
      const eventsData = await eventsResponse.json();
      if (eventsData.success) {
        setEvents(eventsData.data);
      }

      // Buscar receita com filtro de data (apenas se parâmetros fornecidos e não for 'general')
      if (period && period !== 'general' && (period !== 'custom' || customFrom || customTo)) {
        const revenueUrl = new URL(`${baseUrl}/integrations/${provider}/metrics/revenue`);
        Object.entries(dateParams).forEach(([key, value]) => {
          revenueUrl.searchParams.append(key, value);
        });
        
        const revenueResponse = await fetch(revenueUrl.toString());
        const revenueData = await revenueResponse.json();
        if (revenueData.success) {
          setRevenue(revenueData.data);
        }
      } else {
        // Buscar receita geral (sem filtro)
        const revenueResponse = await fetch(`${baseUrl}/integrations/${provider}/metrics/revenue`);
        const revenueData = await revenueResponse.json();
        if (revenueData.success) {
          setRevenue(revenueData.data);
        }
      }

      // Buscar saúde da integração (sempre sem filtro)
      const healthResponse = await fetch(`${baseUrl}/integrations/${provider}/metrics/health`);
      const healthData = await healthResponse.json();
      if (healthData.success) {
        setHealth(healthData.data);
      }

    } catch (err) {
      setError(`Erro ao carregar métricas da ${providerName}`);
      console.error('Erro ao buscar métricas:', err);
    } finally {
      setLoading(false);
    }
  };

  // Funções para proteção por senha
  const handleFinanceTabClick = () => {
    if (activeTab === 1) { // Tab "Receita & Financeiro"
      if (!isFinanceUnlocked) {
        setPasswordDialogOpen(true);
        return;
      }
    }
    setActiveTab(1);
  };

  const handlePasswordSubmit = () => {
    const correctPassword = process.env.REACT_APP_FINANCE_PASSWORD || 'admin123';
    
    if (password === correctPassword) {
      setIsFinanceUnlocked(true);
      setPasswordDialogOpen(false);
      setPassword('');
      setPasswordError('');
      setActiveTab(1);
    } else {
      setPasswordError('Senha incorreta. Tente novamente.');
    }
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPassword('');
    setPasswordError('');
  };

  // Funções para filtro de data
  const handleDateFilterChange = (period: PeriodType) => {
    setDateFilter(period);
    
    // Aplicar filtro automaticamente para períodos pré-definidos
    if (period !== 'custom') {
      fetchData(period);
    }
  };

  const handleCustomDateChange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const handleApplyCustomFilter = () => {
    if (dateFrom && dateTo) {
      fetchData('custom', dateFrom, dateTo);
    }
  };

  // Funções para filtro de vendas geral
  const fetchFilteredSales = async (period: PeriodType, customFrom?: string, customTo?: string) => {
    if (!provider) return;
    
    try {
      const dateParams = getPeriodParams(period, customFrom, customTo);
      
      // Construir URL com parâmetros de data
      const salesUrl = new URL(`${baseUrl}/integrations/${provider}/metrics/sales`);
      Object.entries(dateParams).forEach(([key, value]) => {
        salesUrl.searchParams.append(key, value);
      });
      
      const response = await fetch(salesUrl.toString());
      const data = await response.json();
      
      if (data.success) {
        setFilteredSales(data.data.total_sales);
      }
    } catch (error) {
      console.error('Erro ao buscar vendas filtradas:', error);
    }
  };

  const handleSalesFilterChange = (period: PeriodType) => {
    setSalesFilter(period);
    
    if (period !== 'custom') {
      fetchFilteredSales(period);
    }
  };

  const handleApplySalesFilter = () => {
    if (salesDateFrom && salesDateTo) {
      fetchFilteredSales('custom', salesDateFrom, salesDateTo);
    }
  };

  useEffect(() => {
    fetchData();
  }, [provider]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle color="success" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'error':
        return <Error color="error" />;
      default:
        return <Schedule color="disabled" />;
    }
  };

  const getStatusColor = (status: string): 'error' | 'success' | 'warning' | 'info' => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const handleSendEmail = (customerEmail: string, customerName: string) => {
    const subject = encodeURIComponent('Obrigado pela sua compra!');
    const body = encodeURIComponent(`Olá ${customerName},\n\nObrigado pela sua compra! Em breve você receberá mais informações sobre o seu produto.\n\nAtenciosamente,\nEquipe`);
    window.open(`mailto:${customerEmail}?subject=${subject}&body=${body}`);
  };

  const handleSendWhatsApp = (phoneNumber: string, customerName: string) => {
    const message = encodeURIComponent(`Olá ${customerName}! Obrigado pela sua compra. Como posso ajudá-lo?`);
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    window.open(`https://wa.me/55${cleanPhone}?text=${message}`);
  };

  const getEventTypeColor = (eventType: string) => {
    // Cores genéricas para eventos
    const colors: Record<string, string> = {
      'SALE_APPROVED': '#10B981',
      'APPROVED': '#10B981',
      'PAYMENT_APPROVED': '#10B981',
      'invoice.payment_succeeded': '#10B981',
      'subscription.activated': '#10B981',
      'customer.member_added': '#10B981',
      'PURCHASE_APPROVED': '#10B981',
      'PURCHASE_COMPLETE': '#10B981',
      'BANK_SLIP_GENERATED': '#3B82F6',
      'BANK_SLIP_EXPIRED': '#EF4444',
      'SUBSCRIPTION_RENEWED': '#10B981',
      'SUBSCRIPTION_EXPIRED': '#EF4444',
      'PAYMENT_PENDING': '#F59E0B',
      'PURCHASE_CANCELED': '#EF4444',
      'PURCHASE_EXPIRED': '#EF4444',
      'PURCHASE_REFUNDED': '#EF4444'
    };
    return colors[eventType] || '#6B7280';
  };

  const getEventTypeLabel = (eventType: string) => {
    // Labels genéricos para eventos
    const labels: Record<string, string> = {
      'SALE_APPROVED': 'Venda Aprovada',
      'APPROVED': 'Aprovado',
      'PAYMENT_APPROVED': 'Pagamento Aprovado',
      'invoice.payment_succeeded': 'Pagamento Realizado',
      'subscription.activated': 'Assinatura Ativada',
      'customer.member_added': 'Membro Adicionado',
      'PURCHASE_APPROVED': 'Compra Aprovada',
      'PURCHASE_COMPLETE': 'Compra Completa',
      'BANK_SLIP_GENERATED': 'Boleto Gerado',
      'BANK_SLIP_EXPIRED': 'Boleto Expirado',
      'SUBSCRIPTION_RENEWED': 'Assinatura Renovada',
      'SUBSCRIPTION_EXPIRED': 'Assinatura Expirada',
      'PAYMENT_PENDING': 'Pagamento Pendente',
      'PURCHASE_CANCELED': 'Compra Cancelada',
      'PURCHASE_EXPIRED': 'Compra Expirada',
      'PURCHASE_REFUNDED': 'Compra Reembolsada',
      'digistore_payment': 'Pagamento DigiStore24'
    };
    return labels[eventType] || eventType;
  };

  // Função para extrair dados do cliente do webhook_data baseado no provider
  const extractCustomerData = (webhookData: any, provider: string) => {
    try {
      switch (provider?.toLowerCase()) {
        case 'kirvano':
          return {
            name: webhookData?.customer?.name,
            email: webhookData?.customer?.email,
            phone: webhookData?.customer?.phone_number,
            document: webhookData?.customer?.document
          };
        case 'hubla':
          return {
            name: webhookData?.event?.user?.fullName || webhookData?.event?.lead?.fullName,
            email: webhookData?.event?.user?.email || webhookData?.event?.lead?.email,
            phone: webhookData?.event?.user?.phone || webhookData?.event?.lead?.phone,
            document: null
          };
        case 'hotmart':
          return {
            name: webhookData?.data?.buyer?.name,
            email: webhookData?.data?.buyer?.email,
            phone: webhookData?.data?.buyer?.checkout_phone || webhookData?.data?.buyer?.phone,
            document: null
          };
        case 'digistore24':
          return {
            name: `${webhookData?.buyer_firstname || ''} ${webhookData?.buyer_lastname || ''}`.trim(),
            email: webhookData?.buyer_email,
            phone: webhookData?.buyer_phone,
            document: null
          };
        case 'kiwify':
          // Kiwify tem os dados do cliente em Customer (com C maiúsculo)
          return {
            name: webhookData?.Customer?.full_name || webhookData?.Customer?.first_name || null,
            email: webhookData?.Customer?.email || null,
            phone: webhookData?.Customer?.mobile || null,
            document: webhookData?.Customer?.CPF || null
          };
        default:
          return {
            name: webhookData?.customer?.name || webhookData?.buyer?.name,
            email: webhookData?.customer?.email || webhookData?.buyer?.email,
            phone: webhookData?.customer?.phone || webhookData?.buyer?.phone,
            document: webhookData?.customer?.document
          };
      }
    } catch (error) {
      console.error('Erro ao extrair dados do cliente:', error);
      return { name: null, email: null, phone: null, document: null };
    }
  };

  // Função para extrair produtos do webhook_data baseado no provider
  const extractProducts = (webhookData: any, provider: string) => {
    try {
      switch (provider?.toLowerCase()) {
        case 'kirvano':
          return webhookData?.products || [];
        case 'hubla':
          return webhookData?.event?.products || (webhookData?.event?.product ? [webhookData.event.product] : []);
        case 'hotmart':
          const product = webhookData?.data?.product;
          if (product?.content?.products) {
            return product.content.products;
          }
          return product ? [product] : [];
        case 'digistore24':
          if (webhookData?.product_name && webhookData?.product_id) {
            return [{
              name: webhookData.product_name,
              id: webhookData.product_id
            }];
          }
          return [];
        case 'kiwify':
          // Kiwify tem o produto em Product (com P maiúsculo)
          if (webhookData?.Product) {
            return [{
              product_id: webhookData.Product.product_id,
              name: webhookData.Product.product_name,
              product_name: webhookData.Product.product_name,
              title: webhookData.Product.product_name
            }];
          }
          return [];
        default:
          return webhookData?.products || webhookData?.product ? [webhookData.product] : [];
      }
    } catch (error) {
      console.error('Erro ao extrair produtos:', error);
      return [];
    }
  };

  // Função para extrair valor total do webhook_data baseado no provider
  const extractTotalPrice = (webhookData: any, provider: string) => {
    try {
      switch (provider?.toLowerCase()) {
        case 'kirvano':
          return webhookData?.total_price || 'N/A';
        case 'hubla':
          // Hubla armazena valores em centavos no objeto amount
          if (webhookData?.event?.invoice?.amount?.totalCents) {
            const value = parseFloat(webhookData.event.invoice.amount.totalCents.toString()) / 100;
            return value;
          }
          if (webhookData?.event?.subscription?.amount?.totalCents) {
            const value = parseFloat(webhookData.event.subscription.amount.totalCents.toString()) / 100;
            return value;
          }
          return 'N/A';
        case 'hotmart':
          return webhookData?.data?.purchase?.full_price?.value || webhookData?.data?.purchase?.price?.value || 'N/A';
        case 'digistore24':
          if (webhookData?.amount && webhookData?.currency) {
            return `${webhookData.currency} ${webhookData.amount}`;
          }
          return webhookData?.amount || 'N/A';
        case 'kiwify':
          // Kiwify armazena valores em centavos em Commissions.charge_amount
          if (webhookData?.Commissions?.charge_amount) {
            const cents = parseInt(webhookData.Commissions.charge_amount.toString()) || 0;
            return cents / 100; // Converter centavos para reais
          }
          return 'N/A';
        default:
          // Se for um objeto amount, tentar extrair totalCents
          if (webhookData?.amount && typeof webhookData.amount === 'object' && webhookData.amount.totalCents) {
            return parseFloat(webhookData.amount.totalCents.toString()) / 100;
          }
          return webhookData?.total_price || (typeof webhookData?.amount === 'number' ? webhookData.amount : 'N/A');
      }
    } catch (error) {
      console.error('Erro ao extrair valor total:', error);
      return 'N/A';
    }
  };

  // Função para extrair método de pagamento do webhook_data baseado no provider
  const extractPaymentMethod = (webhookData: any, provider: string) => {
    try {
      switch (provider?.toLowerCase()) {
        case 'kirvano':
          return webhookData?.payment?.method || webhookData?.payment_method || 'N/A';
        case 'hubla':
          return webhookData?.event?.invoice?.paymentMethod || 'N/A';
        case 'hotmart':
          return webhookData?.data?.purchase?.payment?.method || 'N/A';
        case 'digistore24':
          return 'DigiStore24';
        case 'kiwify':
          return webhookData?.payment_method || 'N/A';
        default:
          return webhookData?.payment?.method || webhookData?.payment_method || 'N/A';
      }
    } catch (error) {
      console.error('Erro ao extrair método de pagamento:', error);
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <IconButton color="inherit" size="small" onClick={() => fetchData()}>
            <Refresh />
          </IconButton>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!provider) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Provider não especificado na URL
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: 4,
        p: 3,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/integrations')}
            sx={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
              color: '#000000',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)',
              }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ 
              color: '#ffffff',
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              Vendas & Receita - {providerName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Análise de vendas, produtos e performance comercial da plataforma {providerName}
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={() => fetchData()} 
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#000000',
            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <Refresh />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        mb: 3,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderRadius: 3,
        p: 1,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
      }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => {
            if (newValue === 1) { // Tab "Receita & Financeiro"
              handleFinanceTabClick();
            } else {
              setActiveTab(newValue);
            }
          }}
          sx={{
            '& .MuiTab-root': {
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: 2,
              margin: '0 4px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
              },
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                color: '#000000',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3)'
              }
            }
          }}
        >
          <Tab icon={<Assessment />} label="Dashboard Vendas" />
          <Tab 
            icon={isFinanceUnlocked ? <MonetizationOn /> : <Lock />} 
            label={isFinanceUnlocked ? "Receita & Financeiro" : "Receita & Financeiro 🔒"} 
          />
          <Tab icon={<Receipt />} label="Produtos & Transações" />
          <Tab icon={<Timeline />} label="Timeline de Vendas" />
        </Tabs>
      </Box>

      {/* Tab 1: Dashboard Vendas */}
      {activeTab === 0 && metrics && (
        <Grid container spacing={3}>
          {/* Cards de Métricas de Vendas */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 12px 32px rgba(25, 118, 210, 0.2), 0 4px 16px rgba(25, 118, 210, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Receipt sx={{ 
                    mr: 1, 
                    color: '#1976d2',
                    filter: 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3))'
                  }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Total Transações</Typography>
                </Box>
                <Typography variant="h4" sx={{ 
                  color: '#1976d2',
                  fontWeight: 700,
                  textShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' }
                }}>
                  {metrics.total_webhooks}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Eventos processados
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Card de Vendas Geral com Filtro */}
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp sx={{ 
                    mr: 1, 
                    color: '#8B5CF6',
                    filter: 'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))'
                  }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Vendas Geral</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Typography variant="h4" sx={{ 
                    color: '#8B5CF6',
                    fontWeight: 700,
                    textShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                    fontSize: { xs: '1.8rem', sm: '2.2rem' }
                  }}>
                    {filteredSales !== null ? filteredSales : (metrics?.last_30_days || 0)}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2, fontSize: '0.75rem' }}>
                  {salesFilter === 'general' ? 'Total vendas aprovadas' : `Vendas no período`}
                </Typography>
                
                {/* Filtro de período integrado ao card */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1.5,
                  '& .MuiFormControl-root': {
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                      '&:hover': {
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        background: 'rgba(255, 255, 255, 0.15)'
                      },
                      '&.Mui-focused': {
                        border: '1px solid #8B5CF6',
                        background: 'rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.75rem',
                      '&.Mui-focused': {
                        color: '#8B5CF6'
                      }
                    },
                    '& .MuiSelect-select': {
                      color: '#000000',
                      fontSize: '0.75rem',
                      py: 0.5
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    }
                  },
                  '& .MuiTextField-root': {
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                      color: '#000000',
                      fontSize: '0.75rem',
                      '&:hover': {
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        background: 'rgba(255, 255, 255, 0.15)'
                      },
                      '&.Mui-focused': {
                        border: '1px solid #8B5CF6',
                        background: 'rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.75rem',
                      '&.Mui-focused': {
                        color: '#8B5CF6'
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    }
                  },
                  '& .MuiButton-root': {
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                    color: '#000000',
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                      transform: 'translateY(-1px)'
                    }
                  }
                }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Período</InputLabel>
                    <Select
                      label="Período"
                      value={salesFilter}
                      onChange={(e) => handleSalesFilterChange(e.target.value as any)}
                    >
                      <MenuItem value="general">Geral</MenuItem>
                      <MenuItem value="today">Hoje</MenuItem>
                      <MenuItem value="7d">Últimos 7 dias</MenuItem>
                      <MenuItem value="30d">Últimos 30 dias</MenuItem>
                      <MenuItem value="custom">Personalizado</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {salesFilter === 'custom' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        type="date"
                        label="De"
                        InputLabelProps={{ shrink: true }}
                        value={salesDateFrom}
                        onChange={(e) => setSalesDateFrom(e.target.value)}
                        sx={{ minWidth: 120 }}
                      />
                      <TextField
                        size="small"
                        type="date"
                        label="Até"
                        InputLabelProps={{ shrink: true }}
                        value={salesDateTo}
                        onChange={(e) => setSalesDateTo(e.target.value)}
                        sx={{ minWidth: 120 }}
                      />
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={handleApplySalesFilter}
                        sx={{ minWidth: 80 }}
                      >
                        Aplicar
                      </Button>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp sx={{ 
                    mr: 1, 
                    color: '#10B981',
                    filter: 'drop-shadow(0 2px 4px rgba(16, 185, 129, 0.3))'
                  }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Vendas 30 dias</Typography>
                </Box>
                <Typography variant="h4" sx={{ 
                  color: '#10B981',
                  fontWeight: 700,
                  textShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' }
                }}>
                  {metrics.last_30_days}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Transações no período
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Schedule sx={{ 
                    mr: 1, 
                    color: '#F59E0B',
                    filter: 'drop-shadow(0 2px 4px rgba(245, 158, 11, 0.3))'
                  }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Vendas 7 dias</Typography>
                </Box>
                <Typography variant="h4" sx={{ 
                  color: '#F59E0B',
                  fontWeight: 700,
                  textShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' }
                }}>
                  {metrics.last_7_days}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Transações na semana
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Today sx={{ 
                    mr: 1, 
                    color: '#06B6D4',
                    filter: 'drop-shadow(0 2px 4px rgba(6, 182, 212, 0.3))'
                  }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Vendas Hoje</Typography>
                </Box>
                <Typography variant="h4" sx={{ 
                  color: '#06B6D4',
                  fontWeight: 700,
                  textShadow: '0 2px 4px rgba(6, 182, 212, 0.3)',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' }
                }}>
                  {metrics.today}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Transações hoje
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Gráfico de Status de Vendas */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
              }
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Vendas por Produto
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={revenue && revenue.sales_by_product ? Object.entries(revenue.sales_by_product).map(([name, value]) => ({
                        name: name,
                        value: value
                      })) : []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props: any) => `${props.name}: ${props.value}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {revenue && revenue.sales_by_product ? Object.entries(revenue.sales_by_product).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      )) : []}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name) => [
                        value,
                        'Vendas'
                      ]}
                      contentStyle={{
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Gráfico de Performance de Vendas */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
              }
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance de Vendas
                </Typography>
                <ResponsiveContainer width="100%" height={450}>
                  <BarChart 
                    data={Object.entries(metrics.events_by_type).map(([name, value]) => ({
                      name: getEventTypeLabel(name),
                      value
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      tick={{ fill: '#ffffff', fontSize: 12, fontWeight: 500 }}
                      interval={0}
                    />
                    <YAxis 
                      tick={{ fill: '#ffffff', fontSize: 12 }}
                      tickLine={{ stroke: 'rgba(255, 255, 255, 0.3)' }}
                      axisLine={{ stroke: 'rgba(255, 255, 255, 0.3)' }}
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => [
                        value,
                        'Eventos'
                      ]}
                      contentStyle={{
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                    />
                    <Bar 
                      dataKey="value" 
                      fill="url(#performanceGradient)"
                      radius={[4, 4, 0, 0]}
                    />
                    <defs>
                      <linearGradient id="performanceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" />
                        <stop offset="100%" stopColor="#1D4ED8" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Receita & Financeiro */}
      {activeTab === 1 && (
        <>
          {!isFinanceUnlocked ? (
            // Conteúdo ofuscado quando não desbloqueado
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
                  textAlign: 'center',
                  p: 4
                }}>
                  <CardContent>
                    <Security sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
                    <Typography variant="h4" gutterBottom sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontWeight: 700
                    }}>
                      Área Restrita
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                      Esta seção contém informações financeiras sensíveis
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Lock />}
                      onClick={() => setPasswordDialogOpen(true)}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Digite a senha para acessar
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            // Conteúdo desbloqueado
            <>
              {/* Filtro de Data */}
              <Box sx={{ mb: 3 }}>
                <Card sx={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 3,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
                  p: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                      📅 Filtrar por Período
                    </Typography>
                    <PeriodFilter
                      period={dateFilter}
                      onPeriodChange={handleDateFilterChange}
                      dateFrom={dateFrom}
                      dateTo={dateTo}
                      onDateFromChange={(date) => handleCustomDateChange(date, dateTo)}
                      onDateToChange={(date) => handleCustomDateChange(dateFrom, date)}
                      onApply={handleApplyCustomFilter}
                      showCustomDates={true}
                      size="small"
                    />
                  </Box>
                </Card>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    }
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AttachMoney sx={{ 
                          mr: 1, 
                          color: '#10B981',
                          filter: 'drop-shadow(0 2px 4px rgba(16, 185, 129, 0.3))'
                        }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Receita Total</Typography>
                      </Box>
                      <Typography variant="h3" sx={{ 
                        color: '#10B981',
                        fontWeight: 700,
                        textShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                      }}>
                        {revenue ? formatCurrency(revenue.total_revenue) : 'R$ 0,00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Faturamento acumulado (apenas vendas aprovadas)
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    }
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Receipt sx={{ 
                          mr: 1, 
                          color: '#1976d2',
                          filter: 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3))'
                        }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Total Transações</Typography>
                      </Box>
                      <Typography variant="h3" sx={{ 
                        color: '#1976d2',
                        fontWeight: 700,
                        textShadow: '0 2px 4px rgba(25, 118, 210, 0.3)'
                      }}>
                        {revenue ? revenue.total_transactions : 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vendas aprovadas realizadas
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    }
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TrendingUp sx={{ 
                          mr: 1, 
                          color: '#06B6D4',
                          filter: 'drop-shadow(0 2px 4px rgba(6, 182, 212, 0.3))'
                        }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Ticket Médio</Typography>
                      </Box>
                      <Typography variant="h3" sx={{ 
                        color: '#06B6D4',
                        fontWeight: 700,
                        textShadow: '0 2px 4px rgba(6, 182, 212, 0.3)'
                      }}>
                        {revenue ? formatCurrency(revenue.average_transaction) : 'R$ 0,00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Valor médio por venda aprovada
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                    }
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Vendas por Produto
                      </Typography>
                      <ResponsiveContainer width="100%" height={500}>
                        <BarChart data={revenue && revenue.sales_by_product ? Object.entries(revenue.sales_by_product).map(([product, count]) => ({
                          name: product,
                          vendas: count
                        })) : []} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                          <XAxis 
                            dataKey="name" 
                            angle={-45} 
                            textAnchor="end" 
                            height={120}
                            tick={{ fill: '#ffffff', fontSize: 12, fontWeight: 500 }}
                            interval={0}
                          />
                          <YAxis 
                            tick={{ fill: '#ffffff', fontSize: 12 }}
                            tickLine={{ stroke: 'rgba(255, 255, 255, 0.3)' }}
                            axisLine={{ stroke: 'rgba(255, 255, 255, 0.3)' }}
                          />
                          <RechartsTooltip 
                            formatter={(value, name) => [
                              value,
                              'Vendas'
                            ]}
                            contentStyle={{
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              color: '#ffffff'
                            }}
                          />
                          <Bar 
                            dataKey="vendas" 
                            fill="url(#productGradient)"
                            radius={[4, 4, 0, 0]}
                          />
                          <defs>
                            <linearGradient id="productGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#667eea" />
                              <stop offset="100%" stopColor="#764ba2" />
                            </linearGradient>
                          </defs>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}

      {/* Diálogo de Senha */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handlePasswordDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 700
        }}>
          <Lock sx={{ mr: 1, verticalAlign: 'middle' }} />
          Acesso Restrito
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Digite a senha para acessar as informações financeiras:
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordSubmit();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(8px)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(102, 126, 234, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handlePasswordDialogClose}
            sx={{ color: 'text.secondary' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Acessar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tab 3: Produtos & Transações */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Histórico de Transações
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Line type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Tab 4: Timeline de Vendas */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Histórico Detalhado de Transações
            </Typography>
            <TableContainer component={Paper} sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)'
            }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data/Hora</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Contato</TableCell>
                    <TableCell>Produto</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((event) => {
                    const webhookData = event.webhook_data;
                    const customer = extractCustomerData(webhookData, provider || '');
                    const products = extractProducts(webhookData, provider || '');
                    const totalPrice = extractTotalPrice(webhookData, provider || '');
                    const paymentMethod = extractPaymentMethod(webhookData, provider || '');
                    
                    return (
                      <TableRow key={event.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(event.processed_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {customer.name || event.contact?.name || 'N/A'}
                            </Typography>
                            {customer.document && (
                              <Typography variant="caption" color="text.secondary">
                                {customer.document}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {customer.email || event.contact?.email || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {customer.phone || event.contact?.phone || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {products.map((product: any, index: number) => (
                              <Box key={index} sx={{ mb: 0.5 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {product.name || product.product_name || product.title || 'Produto Desconhecido'}
                                </Typography>
                                {product.price && (
                                  <Typography variant="caption" color="text.secondary">
                                    {product.price}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                            {products.length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                N/A
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {typeof totalPrice === 'number' ? formatCurrency(totalPrice) : totalPrice}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getEventTypeLabel(event.event_type)}
                            size="small"
                            sx={{
                              backgroundColor: getEventTypeColor(event.event_type),
                              color: '#000000'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {paymentMethod}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {(customer.email || event.contact?.email) && (
                              <Tooltip title="Enviar Email">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSendEmail(customer.email || event.contact?.email || '', customer.name || event.contact?.name || '')}
                                  sx={{
                                    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(21, 101, 192, 0.15) 100%)',
                                    backdropFilter: 'blur(8px)',
                                    WebkitBackdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(25, 118, 210, 0.3)',
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.25) 0%, rgba(21, 101, 192, 0.25) 100%)',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.3)'
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                  }}
                                >
                                  <Email fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(customer.phone || event.contact?.phone) && (
                              <Tooltip title="Enviar WhatsApp">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleSendWhatsApp(customer.phone || event.contact?.phone || '', customer.name || event.contact?.name || '')}
                                  sx={{
                                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(56, 142, 60, 0.15) 100%)',
                                    backdropFilter: 'blur(8px)',
                                    WebkitBackdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(76, 175, 80, 0.3)',
                                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.25) 0%, rgba(56, 142, 60, 0.25) 100%)',
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 6px 16px rgba(76, 175, 80, 0.3)'
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                  }}
                                >
                                  <WhatsApp fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

    </Box>
  );
};

export default IntegrationMetrics;

